import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import WishToaster from "./WishToaster";
import useSales from "../services/useSales";
import "./DocumentVerificationModal.css";

const SwalAlert = withReactContent(Swal);

const DocumentVerificationModal = ({ documentFlags, onUpload, onSkip }) => {
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const { fetchOrGenerateStoreLink } = useSales();

  const documentTypes = [
    {
      key: "bank_flag",
      title: "Bank Proof",
      description: "Upload your bank proof for verification",
      icon: "🏦",
      acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    },
    {
      key: "aadhar_flag",
      title: "Aadhaar Card",
      description: "Upload your Aadhaar card for verification",
      icon: "🆔",
      acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    },
    {
      key: "pan_flag",
      title: "PAN Card",
      description: "Upload your PAN card for verification",
      icon: "📄",
      acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    },
    {
      key: "address_flag",
      title: "Address Proof",
      description: "Upload your address proof document",
      icon: "🏠",
      acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    },
  ];

  const missingDocuments = documentTypes.filter((doc) => documentFlags[doc.key] === 0);

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    setUploadingFiles((prev) => ({ ...prev, [documentType]: true }));

    try {
      // Simulate file upload - replace with actual upload logic
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setUploadedFiles((prev) => ({ ...prev, [documentType]: file.name }));

      // Call the upload callback
      if (onUpload) {
        onUpload(documentType, file);
      }

      // Show success toast
      WishToaster({
        toastType: "success",
        toastMessage: `${documentType} has been uploaded successfully!`,
      });
    } catch (error) {
      WishToaster({
        toastType: "error",
        toastMessage: "Failed to upload file. Please try again.",
      });
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false);

  const handleSkip = () => {
    setShowSkipConfirmation(true);
  };

  const handleSkipConfirm = (confirmed) => {
    if (confirmed) {
      Swal.close();
      if (onSkip) {
        onSkip();
      }
    } else {
      setShowSkipConfirmation(false);
    }
  };

  return (
    <div className="doc-verification-modal">
      <div className="doc-verification-header">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h4>📋 Mandatory reverification of KYDS documents</h4>
            <p>
              This is to inform you that to comply with the policies of the land we need to update certain documents for
              all the direct sellers. It is mandatory to update all the documents which are discrepant though it might
              have been uploaded earlier.
              <br />
              <strong> Kindly upload all the documents latest by 29 September 2025.</strong> <br />
              Since it is connected to statute compliance, failing to upload the same will result in having no access to
              other features of the VO and your commission shall be on hold.
            </p>
          </div>
        </div>
      </div>

      <div className="doc-verification-body">
        <div className="doc-verification-grid">
          {missingDocuments.map((doc) => (
            <div key={doc.key} className="doc-verification-tile">
              <div className="doc-verification-icon">{doc.icon}</div>
              <div className="doc-verification-info">
                <h6>{doc.title}</h6>
                <p>{doc.description}</p>
              </div>
              <div className="doc-verification-actions">
                {uploadedFiles[doc.key] ? (
                  <div className="doc-verification-success">
                    <span className="text-success">✅ {uploadedFiles[doc.key]}</span>
                  </div>
                ) : (
                  <div className="doc-verification-upload-section">
                    <input
                      type="file"
                      id={`doc-file-${doc.key}`}
                      accept={doc.acceptedTypes}
                      onChange={(e) => handleFileUpload(doc.key, e.target.files[0])}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor={`doc-file-${doc.key}`}
                      className={`doc-verification-btn doc-verification-btn-primary doc-verification-btn-sm ${
                        uploadingFiles[doc.key] ? "doc-verification-disabled" : ""
                      }`}
                    >
                      {uploadingFiles[doc.key] ? (
                        <>
                          <span
                            className="doc-verification-spinner doc-verification-spinner-sm me-2"
                            role="status"
                          ></span>
                          Uploading...
                        </>
                      ) : (
                        "📁 Upload"
                      )}
                    </label>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="doc-verification-footer">
        <div className="w-100">
          {/* Access Restriction Alert and Store Link - Only show when skip_flag is 1 and documents are missing */}
          {documentFlags.skip_flag === 1 && missingDocuments.length > 0 && (
            <>
              <div className="alert alert-warning mb-1" role="alert">
                <i className="las la-exclamation-triangle" style={{ marginRight: "5px" }}></i>
                <strong>Access Restricted:</strong> Complete document verification to access all features. You can still
                use your store link for sales.
              </div>

              {/* Store Link Section */}
              <div className="mb-4 text-center">
                <button
                  type="button"
                  className="doc-verification-btn doc-verification-btn-secondary"
                  onClick={() => {
                    fetchOrGenerateStoreLink(false, (data) => {
                      navigator.clipboard.writeText(data);
                      WishToaster({
                        toastMessage: "Store link copied to clipboard!",
                        toastType: "success",
                      });
                    });
                  }}
                >
                  🔗 Copy Store Link
                </button>
              </div>
            </>
          )}

          {/* Skip Button - Only show when skip_flag is 0 */}
          <div className="d-flex justify-content-center">
            {documentFlags.skip_flag === 0 &&
              (showSkipConfirmation ? (
                <div className="doc-skip-confirm">
                  <div className="doc-skip-content">
                    <div className="doc-skip-icon">
                      <i className="las la-exclamation-triangle"></i>
                    </div>
                    <div className="doc-skip-title">You're about to skip document verification</div>
                    <div className="doc-skip-note">
                      Document approval may take up to<span className="doc-deadline-pill"> 3 days</span> from the date
                      of upload. We request you not to wait until the last date to upload your documents, in order to
                      avoid restricted VO access and commission holds.
                    </div>
                    <div className="doc-skip-actions">
                      <button
                        type="button"
                        className="doc-verification-btn doc-verification-btn-danger"
                        onClick={() => handleSkipConfirm(true)}
                      >
                        Yes, skip for now
                      </button>
                      <button
                        type="button"
                        className="doc-verification-btn doc-verification-btn-primary"
                        onClick={() => handleSkipConfirm(false)}
                      >
                        No, I'll upload
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="doc-verification-btn doc-verification-btn-warning"
                  onClick={handleSkip}
                >
                  Skip for Now
                </button>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentVerificationModal;
